import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import JoinScreen from '../components/participant/JoinScreen';
import WaitingLobby from '../components/participant/WaitingLobby';
import AnswerButtons from '../components/participant/AnswerButtons';
import CorrectAnswer from '../components/participant/CorrectAnswer';
import IncorrectAnswer from '../components/participant/IncorrectAnswer';
import { SessionProvider, useSession } from '../context/SessionContext';
import { getQuiz } from '../services/quizService';
import { isQuestionRevealed } from '../utils/quizState';

const STORAGE_KEY = 'mikkelkviss_participant';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

function getSavedParticipant() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after 24 hours
    if (Date.now() - data.joinedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function clearSavedParticipant() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

function ParticipantContent({ participantId, participantName }) {
  const { session, loading, error } = useSession();
  const [quiz, setQuiz] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [streak, setStreak] = useState(0);
  const [, setRevealTick] = useState(0);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    if (session?.quizId) {
      getQuiz(session.quizId)
        .then(setQuiz)
        .catch(err => console.error('Error loading quiz:', err));
    }
  }, [session?.quizId]);

  // Reset answer result when question changes
  useEffect(() => {
    setAnswerResult(null);
  }, [session?.currentQuestionIndex]);

  // While waiting for reveal, tick every 500ms so the timer-based branch of
  // isQuestionRevealed picks up when the question timer expires.
  useEffect(() => {
    if (session?.status !== 'active') return;
    const interval = setInterval(() => setRevealTick((t) => t + 1), 500);
    return () => clearInterval(interval);
  }, [session?.status, session?.currentQuestionIndex]);

  // Wake Lock — prevent screen from sleeping during active quiz
  useEffect(() => {
    if (session?.status !== 'active' && session?.status !== 'waiting') {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      return;
    }

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* Wake Lock not supported or denied */ }
    };

    requestWakeLock();

    // Re-request on visibility change (wake lock released on tab switch)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [session?.status]);

  const handleAnswerResult = (result) => {
    setAnswerResult(result);
    if (result.isCorrect) {
      setStreak(prev => prev + 1);
    } else {
      setStreak(0);
    }
  };

  if (loading || !quiz) {
    return (
      <div className="min-h-screen bg-mp-darkgreen flex items-center justify-center font-body">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mp-lime mx-auto mb-4"></div>
          <div className="font-display text-white text-xl">Lastar økt...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-mp-darkgreen flex items-center justify-center p-4 font-body">
        <div className="bg-white rounded-2xl p-8 max-w-md border border-[rgba(163,0,97,0.20)] shadow-mp-2">
          <h2 className="font-display text-mp-magenta text-xl text-center mb-2">Feil</h2>
          <p className="font-body text-mp-stone-700 text-center">{error}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-mp-darkgreen flex items-center justify-center font-body">
        <div className="font-display text-white text-2xl">Fann ikkje økta</div>
      </div>
    );
  }

  const participant = session.participants?.[participantId];

  // Status: Waiting
  if (session.status === 'waiting') {
    return <WaitingLobby participantName={participantName || participant?.name} />;
  }

  // Status: Active
  if (session.status === 'active') {
    const currentQuestion = quiz.questions[session.currentQuestionIndex];
    const questionId = currentQuestion.id;
    const hasAnswered = participant?.answers?.[questionId] !== undefined;

    if (!currentQuestion) {
      return (
        <div className="min-h-screen bg-mp-darkgreen flex items-center justify-center font-body">
          <div className="font-display text-white text-2xl">Lastar spørsmål …</div>
        </div>
      );
    }

    // Show feedback screen only after the question has been revealed
    // (timer expired OR all participants have answered). Until then the
    // participant sees the "Svar sendt – ventar på resultat" screen rendered
    // by AnswerButtons when hasAnswered=true.
    const revealed = isQuestionRevealed(session, quiz);
    if (answerResult && revealed) {
      if (answerResult.isCorrect) {
        return (
          <CorrectAnswer
            points={answerResult.pointsEarned}
            totalScore={participant?.score || 0}
            streak={streak}
            questionNumber={session.currentQuestionIndex + 1}
            totalQuestions={quiz.questions.length}
          />
        );
      } else {
        return (
          <IncorrectAnswer totalScore={participant?.score || 0} />
        );
      }
    }

    // Show answer buttons
    return (
      <AnswerButtons
        sessionId={session.id}
        participantId={participantId}
        question={currentQuestion}
        questionId={questionId}
        hasAnswered={hasAnswered}
        participantName={participantName || participant?.name}
        score={participant?.score}
        onAnswerResult={handleAnswerResult}
      />
    );
  }

  // Status: Finished
  if (session.status === 'finished') {
    const participants = Object.values(session.participants || {});
    const sortedParticipants = participants.sort((a, b) => b.score - a.score);
    const rank = sortedParticipants.findIndex(p => p.id === participantId) + 1;

    let emoji = '🎉';
    if (rank === 1) emoji = '🥇';
    else if (rank === 2) emoji = '🥈';
    else if (rank === 3) emoji = '🥉';

    return (
      <div className="min-h-screen bg-mp-darkgreen flex items-center justify-center p-4 font-body">
        <div className="relative">
          {/* Background glow */}
          <div className="absolute inset-0 bg-mp-lime/30 blur-3xl rounded-full scale-150"></div>

          <div className="relative bg-white rounded-3xl p-8 max-w-md text-center border border-[rgba(17,73,66,0.10)] shadow-mp-3">
            <div className="text-7xl mb-6">{emoji}</div>
            <h1 className="font-display text-mp-darkgreen text-4xl mb-2">
              Kvissen er ferdig!
            </h1>
            <div className="font-body text-mp-stone-700 text-lg mb-6">
              Du enda på <span className="text-mp-magenta font-display">#{rank}</span> av {participants.length}
            </div>
            <div className="bg-mp-lime text-mp-darkgreen rounded-2xl p-6 shadow-mp-2">
              <div className="font-display text-[11px] uppercase tracking-[0.18em] opacity-70 mb-2">Sluttpoeng</div>
              <div className="font-display text-5xl">
                {(participant?.score || 0).toLocaleString()}
              </div>
            </div>
            <p className="font-body text-mp-stone-500 text-sm mt-6">
              Sjå storskjermen for full resultatliste!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default function ParticipantPage() {
  const { sessionId: urlSessionId } = useParams();
  const [joined, setJoined] = useState(null);
  const [checking, setChecking] = useState(true);

  // Auto-rejoin from localStorage on mount
  useEffect(() => {
    const tryRejoin = async () => {
      const saved = getSavedParticipant();
      if (!saved) { setChecking(false); return; }

      // If URL has a different sessionId, ignore saved data
      if (urlSessionId && urlSessionId !== saved.sessionId) {
        clearSavedParticipant();
        setChecking(false);
        return;
      }

      try {
        // Verify session still exists and participant is in it
        const sessionRef = doc(db, 'sessions', saved.sessionId);
        const sessionSnap = await getDoc(sessionRef);

        if (sessionSnap.exists()) {
          const data = sessionSnap.data();
          if (data.participants?.[saved.participantId]) {
            // Session and participant both exist — rejoin
            setJoined(saved);
            setChecking(false);
            return;
          }
        }
      } catch { /* Firestore error — fall through to join screen */ }

      // Session or participant gone — clear saved data
      clearSavedParticipant();
      setChecking(false);
    };

    tryRejoin();
  }, [urlSessionId]);

  const sessionId = joined?.sessionId || urlSessionId;

  if (checking) {
    return (
      <div className="min-h-screen bg-mp-darkgreen flex items-center justify-center font-body">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mp-lime mx-auto mb-4" />
          <div className="font-display text-white text-xl">Koplar til …</div>
        </div>
      </div>
    );
  }

  if (!sessionId && !joined) {
    return <JoinScreen onJoined={setJoined} />;
  }

  return (
    <SessionProvider sessionId={sessionId}>
      <ParticipantContent
        participantId={joined?.participantId}
        participantName={joined?.participantName}
      />
    </SessionProvider>
  );
}
